import React from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuButton, MenuList, MenuItem, Button, useColorModeValue } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

const languages = [
  { code: 'ko', label: 'KR', display: '한국어' },
  { code: 'en', label: 'EN', display: 'English' },
  { code: 'zh', label: 'CN', display: '中文' },
  { code: 'ja', label: 'JP', display: '日本語' },
];

// `onDark` is set when the navbar floats over the home hero, where the surface is near-black.
export default function LanguageSwitcher({ onDark = false }) {
  const { i18n } = useTranslation();
  const currentLang =
    languages.find((l) => l.code === i18n.language?.substring(0, 2)) || languages[0];

  const lightBg = useColorModeValue('white', 'gray.700');
  const lightBorder = useColorModeValue('#D3DCE8', 'whiteAlpha.400');
  const lightColor = useColorModeValue('#101A2E', 'white');
  const lightHoverBg = useColorModeValue('#F2F6FB', 'whiteAlpha.200');
  const accent = useColorModeValue('brand.500', 'brand.300');

  // Over the hero the control stays translucent so the navbar keeps its floating look, but the
  // white outline and label keep it obviously interactive.
  const bg = onDark ? 'rgba(255, 255, 255, 0.10)' : lightBg;
  const border = onDark ? 'rgba(255, 255, 255, 0.42)' : lightBorder;
  const color = onDark ? 'white' : lightColor;
  const hoverBg = onDark ? 'rgba(255, 255, 255, 0.20)' : lightHoverBg;
  const hoverBorder = onDark ? 'white' : accent;

  return (
    <Menu>
      <MenuButton
        as={Button}
        aria-label={currentLang.display}
        size="sm"
        minW="74px"
        h="34px"
        px="12px"
        rightIcon={<ChevronDownIcon />}
        bg={bg}
        color={color}
        border="1px solid"
        borderColor={border}
        borderRadius="4px"
        fontFamily="mono"
        fontSize="12px"
        fontWeight="700"
        letterSpacing="0.08em"
        boxShadow="0px 1px 2px rgba(16, 26, 46, 0.06)"
        _hover={{ bg: hoverBg, borderColor: hoverBorder }}
        _active={{ bg: hoverBg }}
        _focusVisible={{
          outline: '2px solid',
          outlineColor: 'brand.400',
          outlineOffset: '2px',
        }}
      >
        {currentLang.label}
      </MenuButton>
      <MenuList minW="150px" py="4px" zIndex="popover">
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => i18n.changeLanguage(lang.code)}
            fontSize="14px"
            fontWeight={lang.code === currentLang.code ? '700' : '400'}
            /* The dropdown keeps the normal surface even when the button is over the hero,
               so it uses the accent rather than the button's white outline colour. */
            color={lang.code === currentLang.code ? accent : undefined}
          >
            {lang.display}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}
